"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS7 } from "./DevaKathaLS7";
import { DevaKathaLS10 } from "./DevaKathaLS10";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Kamakhya phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (no opener shift — doc has no AE opener).
// All phrase start times frame-locked to word_timestamps_hindi. 1280×704 @ 16fps.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 19 selective phrases across the 1184s doc (खिड़कियां बंद dropped). Numerals stay Latin (25, 1553, 379, 5).
export const KAMA_PHRASES: Phrase[] = [
  // Hook (Part 0) cards — frame-locked to the hook word_timestamps (शाप 3.80 · पत्थर की मूरत 15.21 · महर्षि 20.30)
  { start: 3.4,     out: 5.6,     lines: [[{ t: "मां का", c: "y" }, { t: "दिया", c: "y" }], [{ t: "शाप", c: "r", big: true }]] },
  { start: 14.9,    out: 17.0,    lines: [[{ t: "पत्थर", c: "w", big: true }, { t: "की", c: "y" }], [{ t: "मूर्ति", c: "w", big: true }]] },
  { start: 19.9,    out: 22.0,    lines: [[{ t: "महर्षि", c: "w" }, { t: "का", c: "y" }], [{ t: "शाप", c: "r", big: true }]] },
  { start: 28.255,    out: 31.995,    lines: [[{ t: "25", c: "w", big: true }, { t: "लाख", c: "y" }], [{ t: "श्रद्धालु", c: "y", small: true }]] },
  { start: 37.955,   out: 41.395,   lines: [[{ t: "कोई", c: "y" }, { t: "मूर्ति", c: "w", big: true }], [{ t: "नहीं?", c: "r", big: true }]] },
  { start: 72.325,   out: 75.895,   lines: [[{ t: "घोस्ट", c: "r", big: true }, { t: "आर्मी", c: "w", big: true }]] },
  { start: 118.166,  out: 121.716,  lines: [[{ t: "6", c: "w", big: true }, { t: "फुट", c: "w", big: true }], [{ t: "नीचे", c: "y" }]] },
  { start: 136.336,  out: 139.816,  lines: [[{ t: "गीला", c: "y" }, { t: "पत्थर", c: "w", big: true }]] },
  { start: 216.976,  out: 220.416,  lines: [[{ t: "योनि", c: "y" }, { t: "चक्र", c: "w", big: true }]] },
  { start: 253.596,  out: 257.116,  lines: [[{ t: "देवी का", c: "y" }], [{ t: "रजस्वला", c: "r", big: true }]] },
  { start: 279.236,  out: 282.716,  lines: [[{ t: "वामाचार", c: "y", big: true }]] },
  { start: 308.406,  out: 311.916,  lines: [[{ t: "नरकासुर", c: "r", big: true }]] },
  { start: 361.556,  out: 365.116,  lines: [[{ t: "अधूरी", c: "w", big: true }], [{ t: "सीढ़ियां", c: "y" }]] },
  { start: 381.206,  out: 384.716,  lines: [[{ t: "महर्षि", c: "w" }, { t: "वशिष्ठ", c: "y", big: true }]] },
  { start: 411.866,  out: 415.316,  lines: [[{ t: "पहाड़", c: "w", big: true }], [{ t: "शक्तिहीन", c: "r", big: true }]] },
  { start: 466.446,  out: 469.916,  lines: [[{ t: "पंचमकार", c: "r", big: true }]] },
  { start: 539.236,  out: 542.716,  lines: [[{ t: "रक्त", c: "r" }, { t: "वस्त्र", c: "r", big: true }]] },
  { start: 599.226,  out: 602.816,  lines: [[{ t: "मादा बलि", c: "w", big: true }], [{ t: "वर्जित", c: "r", big: true }]] },
  { start: 661.136,  out: 664.716,  lines: [[{ t: "1553", c: "w", big: true }], [{ t: "काला", c: "r" }, { t: "साल", c: "r", big: true }]] },
  { start: 842.976,  out: 846.616,  lines: [[{ t: "पूरा", c: "w" }, { t: "राजवंश", c: "y", big: true }], [{ t: "मिटा दूंगी", c: "r", big: true }]] },
  { start: 910.266,  out: 913.816,  lines: [[{ t: "379", c: "w", big: true }], [{ t: "मूर्तियां", c: "y" }]] },
  { start: 1046.366, out: 1049.916, lines: [[{ t: "सिस्मिक", c: "y" }], [{ t: "ज़ोन", c: "w" }, { t: "5", c: "r", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-left viewing tip — white, NO holes mask. Shows over the opener.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op,
                  display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x
      </span>
      <span style={{ color: WHITE, fontSize: Math.round(SMALL_FS * 0.7), fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        for best experience
      </span>
    </div>
  );
};

// Center "हर हर महादेव" chant card — double-quoted Devanagari, gold, gentle fade + pulse. NO holes.
const HarHarCard: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.45, out - 0.5, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const sc = interpolate(t, [start, start + 0.6], [0.9, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const pulse = 1 + 0.02 * Math.sin((t - start) * 3.2);
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <span style={{
        fontFamily, color: YELLOW, fontSize: 84, fontWeight: 900, letterSpacing: "0.01em",
        textShadow: "0 6px 18px rgba(0,0,0,0.92), 0 2px 6px rgba(0,0,0,0.95)",
        opacity: op, transform: `scale(${sc * pulse})`,
      }}>“हर हर महादेव”</span>
    </AbsoluteFill>
  );
};

// "आगे देखिए" teaser with blinking/cycling dots ("" → . → .. → ...). Holes-distress mask
// (same as phrases), text YELLOW / dots WHITE, LEFT-aligned at left:PAD, top 80%.
// Standalone comp `kama-age-dekhiye` — composited onto the finished video over its own window.
export const KamaAgeDekhiye: React.FC = () => {
  const f = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = f / fps;
  const n = Math.floor(((f / fps) * 1.5) % 4); // 0,1,2,3 → "", ".", "..", "..."
  // 0:00 → 1:16 (76s): fade in 0.4s, hold, fade out over the last 1s
  const op = interpolate(t, [0, 0.4, 75.0, 76.0], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{
      backgroundColor: "transparent",
      WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
      WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
      WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
      WebkitMaskPosition: "0 0", maskPosition: "0 0",
    }}>
      <div style={{
        position: "absolute", top: "80%", left: PAD, opacity: op,
        fontFamily, fontWeight: 800, fontSize: Math.round(BASE_FS * 0.8), color: YELLOW, textShadow: SH,
        whiteSpace: "nowrap",
      }}>
        आगे देखिए
        <span style={{ display: "inline-block", width: "1.4em", textAlign: "left", color: WHITE }}>
          {".".repeat(n)}
        </span>
      </div>
    </AbsoluteFill>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip, har-har card, LS pills).
export const KamakhyaOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = KAMA_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-RIGHT), first 5 seconds */}
      <WatchTag fps={fps} start={0.3} out={5.0} />

      {/* "हर हर महादेव" closing chant card @ the final spoken line (1183.9s) */}
      <HarHarCard fps={fps} start={1196.5} out={1200.2} />

      {/* Like+Subscribe (both @ 1.5x) — LS7 @ first "सब्सक्राइब" 68.43s · LS10 @ outro "सब्सक्राइब" 1177.88s */}
      <Sequence from={S(81.716)} durationInFrames={S(5.2)}><DevaKathaLS7 /></Sequence>
      <Sequence from={S(1191.216)} durationInFrames={S(5.2)}><DevaKathaLS10 /></Sequence>

      {/* आगे देखिए — now baked into the main comp (continuous 0:00→1:16, self-fades via its own opacity) */}
      <KamaAgeDekhiye />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
